/**
 * The two card faces, drawn once each and kept.
 *
 * ============================== WHERE THIS CAME FROM ============================
 * `Poker assets/source/cards-bundle/cards.js` is the face generator that came with the
 * art: it composes a card as SVG over the aged stock PNG — corner indices, the pip
 * grid for the spot cards, the engraved ace, and a framed court mirrored top to
 * bottom. `Poker assets/source/gencompact.mjs` is its sibling, the small face.
 *
 * Both are ported here rather than loaded from the assets folder, because the assets
 * folder is not part of this repository and a component cannot import out of it. The
 * geometry is theirs, unchanged — every number below is the number that was in the
 * mock. Three things did change, and each is marked at the line:
 *
 *   1. COLOUR IS NOT BAKED IN. The originals carried `RED = '#9E2B22'` and
 *      `INK = '#0B0F0D'`, which are the mock palette, not the Frontier Book's — the
 *      book's are #7A2020 and #18130F, and `tests/palette.test.ts` retires a whole
 *      list of near-misses exactly like these because none of them was ever noticed by
 *      eye. The faces are inlined into the document rather than loaded as images, so
 *      they can say `var(--art-oxblood)` and inherit the one palette like everything
 *      else. A `data:` URI in an `<img>` would have baked the mock hexes into 52 cards.
 *   2. Paths point at `/art/`, where `scripts/sync-art.mjs` puts things.
 *   3. Fonts come from the kit's tokens rather than being named twice.
 *
 * ## Two faces, chosen by size, and why the small one is not just the big one shrunk
 *
 * A traditional face at 30px is a cream rectangle with a smudge in the corner. The
 * compact face — the rank set large across the top half, one big pip under it — is the
 * same information at a size a person can actually read, and it is what the board, the
 * opponents' cards and the dealer's up-card use. Only the hero's own cards, which are
 * never smaller than about 62px, get courts and a pip grid.
 *
 * "10", never "T". The engine's rank alphabet is "23456789TJQKA" because a rank has to
 * be one character to index by; a card has never had a T printed on it.
 *
 * ## Cached
 *
 * 52 full faces and 52 compact ones, built the first time each is asked for and kept
 * for the session. A face is a pure function of (rank, suit, size), so the cache can
 * never be stale, and re-composing a string on every re-render of a table that
 * re-renders on every action is exactly the kind of waste that shows up as jank.
 * ===============================================================================
 */
import PIPS from "./pips.json";
import { cardName } from "../lib/table-game";

/** The stock is 1500x2023 and every coordinate below is in its pixels. */
const W = 1500, H = 2023;
const SUIT_NAME: Record<string, "spade" | "heart" | "diamond" | "club"> =
  { s: "spade", h: "heart", d: "diamond", c: "club" };

/** (1) The two colours, as tokens rather than hexes. See the header. */
const RED = "var(--art-oxblood)";
const INK = "var(--art-ink)";
/** (2) Where sync-art.mjs put the stock, the pips and the courts. */
const ART = "/art/cards/";

/**
 * The aged stock every card is printed on.
 *
 * `@1x` — 750x1011 — rather than the 1500px tier. The largest a card is ever drawn is
 * about 150px, so the half-scale stock is still five times the pixels it can use, and
 * it is a third of the bytes. The full tier stays in `public/art/` for print.
 */
const STOCK = `${ART}face@1x.png`;

/**
 * (3) The court art that actually exists.
 *
 * `export/cards/courts-web/Ks.webp` is a ZERO-BYTE FILE — the print tier rendered and
 * the 600px conversion produced nothing. Rather than a broken image on the king of
 * spades, the generator falls back to its own placeholder (the big italic rank) for
 * any court it has no art for, which is the behaviour the original already had for the
 * case where court art did not exist at all.
 */
const COURTS = new Set([
  "Jc", "Jd", "Jh", "Js",
  "Qc", "Qd", "Qh", "Qs",
  "Kc", "Kd", "Kh", /* "Ks" — missing from export/, see above */
]);

const pipPath = (suit: string): string => PIPS[SUIT_NAME[suit]!]!.d;

/* ------------------------------------------------------------ the full face --- */

/** The pip grid, in card units. Straight from `cards.js`. */
const X = { l: 0.30, c: 0.50, r: 0.70 } as const;
const Y = {
  t: 0.22, u: 0.36, m: 0.50, v: 0.64, b: 0.78,
  tt: 0.28, bb: 0.72, ty: 0.40, by: 0.60,
} as const;
type XKey = keyof typeof X;
type YKey = keyof typeof Y;

const LAYOUT: Record<string, readonly (readonly [XKey, YKey])[]> = {
  "2":  [["c","t"],["c","b"]],
  "3":  [["c","t"],["c","m"],["c","b"]],
  "4":  [["l","t"],["r","t"],["l","b"],["r","b"]],
  "5":  [["l","t"],["r","t"],["c","m"],["l","b"],["r","b"]],
  "6":  [["l","t"],["r","t"],["l","m"],["r","m"],["l","b"],["r","b"]],
  "7":  [["l","t"],["r","t"],["c","ty"],["l","m"],["r","m"],["l","b"],["r","b"]],
  "8":  [["l","t"],["r","t"],["c","ty"],["l","m"],["r","m"],["c","by"],["l","b"],["r","b"]],
  "9":  [["l","t"],["r","t"],["l","ty"],["r","ty"],["c","m"],["l","by"],["r","by"],["l","b"],["r","b"]],
  "10": [["l","t"],["r","t"],["c","tt"],["l","ty"],["r","ty"],["l","by"],["r","by"],["c","bb"],["l","b"],["r","b"]],
};

function pip(suit: string, cx: number, cy: number, size: number, flip: boolean): string {
  const s = size / 100;
  const t = `translate(${cx - size / 2} ${cy - size / 2}) scale(${s})` +
            (flip ? " rotate(180 50 50)" : "");
  return `<path d="${pipPath(suit)}" fill="currentColor" fill-rule="evenodd" transform="${t}"/>`;
}

function fullFace(rank: string, suit: string): string {
  const parts: string[] = [];
  parts.push(`<image href="${STOCK}" width="${W}" height="${H}"/>`);

  // The enlarged corner index: rank over a small pip, top-left and rotated bottom-right.
  const ixSize = 230, ipSize = 165, ix = 175, iy = 290;
  const index = (flip: boolean): string => {
    const g =
      `<text x="${ix}" y="${iy}" font-family="var(--art-index)" font-weight="700"` +
      ` font-size="${ixSize}" text-anchor="middle" fill="currentColor">${rank}</text>` +
      pip(suit, ix, iy + 130, ipSize, false);
    return flip ? `<g transform="rotate(180 ${W / 2} ${H / 2})">${g}</g>` : g;
  };
  parts.push(index(false), index(true));

  if (rank === "A") {
    // The engraved ace pip. The spade is the house card, so it is drawn larger.
    const sz = suit === "s" ? 900 : 700;
    parts.push(
      `<image href="${ART}pips/${SUIT_NAME[suit]}-engraved.png"` +
      ` x="${W / 2 - sz / 2}" y="${H / 2 - sz / 2}" width="${sz}" height="${sz}"/>`);
  } else if (LAYOUT[rank]) {
    const size = rank === "2" || rank === "3" ? 320 : 260;
    for (const [xk, yk] of LAYOUT[rank]!) {
      parts.push(pip(suit, X[xk] * W, Y[yk] * H, size, Y[yk] > 0.5));
    }
  } else {
    // A court: a framed portrait mirrored top and bottom, pips in opposite corners.
    const fx = 300, fy = 400, fw = W - 600, fh = H - 800;
    parts.push(`<rect x="${fx}" y="${fy}" width="${fw}" height="${fh}" fill="none"` +
               ` stroke="${INK}" stroke-width="6"/>`);
    const key = rank + suit;
    if (COURTS.has(key)) {
      const art = `${ART}courts/${key}.webp`;
      const half = fh / 2;
      // The clip id is namespaced by the card, because 52 faces can be in one document
      // at once on the contact sheet and duplicate ids make one card clip another.
      const cid = `ct-${key}`;
      parts.push(`<clipPath id="${cid}"><rect x="${fx}" y="${fy}" width="${fw}" height="${half}"/></clipPath>`);
      const img = `<image href="${art}" x="${fx}" y="${fy}" width="${fw}" height="${fh * 0.6}"` +
                  ` clip-path="url(#${cid})" preserveAspectRatio="xMidYMin slice"/>`;
      parts.push(img);
      parts.push(`<g transform="rotate(180 ${W / 2} ${H / 2})">${img}</g>`);
      parts.push(`<line x1="${fx}" y1="${H / 2}" x2="${fx + fw}" y2="${H / 2}"` +
                 ` stroke="${INK}" stroke-width="4"/>`);
    } else {
      parts.push(`<text x="${W / 2}" y="${H / 2 + 120}" font-family="var(--art-index)"` +
                 ` font-style="italic" font-size="420" text-anchor="middle"` +
                 ` fill="currentColor" opacity="0.85">${rank}</text>`);
    }
    parts.push(pip(suit, fx + 90, fy + 90, 140, false),
               pip(suit, fx + fw - 90, fy + fh - 90, 140, true));
  }
  return svg(parts.join(""), suit);
}

/* --------------------------------------------------------- the compact face --- */

/**
 * The rank large across the top half, one big pip under it.
 *
 * Set in Atkinson Hyperlegible rather than the index serif: at 30px this is the only
 * thing on the card and it has to survive being that small, which is the exact job the
 * face was drawn for. "10" is set a size smaller so two digits fit the same width.
 */
function compactFace(rank: string, suit: string): string {
  const size = rank === "10" ? 760 : 860;
  const pipSize = 720;
  const px = W / 2 - pipSize / 2, py = H * 0.50;
  const body =
    /*
     * The stock goes under the compact face too.
     *
     * `gencompact.mjs` leaves it out because the MOCK supplied it as a CSS background
     * on the card element — so the mock's small cards sit on aged paper and the ported
     * ones sat on a flat near-white box. Side by side against the mock, that one
     * difference is most of why the board looked like a printout rather than a deck.
     */
    `<image href="${STOCK}" width="${W}" height="${H}"/>` +
    `<text x="${W / 2}" y="${H * 0.42}" font-family="var(--art-numeric)" font-weight="700"` +
    ` font-size="${size}" text-anchor="middle" fill="currentColor">${rank}</text>` +
    `<path d="${pipPath(suit)}" fill="currentColor" fill-rule="evenodd"` +
    ` transform="translate(${px} ${py}) scale(${pipSize / 100})"/>`;
  return svg(body, suit);
}

/**
 * The wrapper both faces share.
 *
 * `color` on the root rather than a fill on every path: one place decides whether this
 * card is a red one, and the pips, the rank and the indices all inherit it. That is
 * also what lets the ONE palette reach inside an SVG — `currentColor` resolves through
 * the CSS custom property, so nothing here carries a hex.
 */
function svg(body: string, suit: string): string {
  const color = suit === "h" || suit === "d" ? RED : INK;
  return `<svg xmlns="http://www.w3.org/2000/svg" class="gc-face" viewBox="0 0 ${W} ${H}"` +
         ` preserveAspectRatio="xMidYMid slice" style="color:${color}"` +
         ` aria-hidden="true" focusable="false">${body}</svg>`;
}

/* ---------------------------------------------------------------- the cache --- */

export type Face = "full" | "compact";

const cache = new Map<string, string>();

/** The engine's rank alphabet has a T where a card has a 10. */
const printedRank = (r: string): string => (r === "T" ? "10" : r);
/** Suit index 0..3 is c/d/h/s, the evaluator's order and the deck's. */
const SUIT_LETTER = ["c", "d", "h", "s"] as const;

/**
 * One card's face, built once.
 *
 * Takes the engine's card number rather than a rank and a suit, so no caller has to
 * know the encoding, and so the trainer's dealt cards and the table's are the same
 * lookup.
 */
export function cardFace(card: number, face: Face): string {
  const { rank, suit } = cardName(card);
  return faceFor(printedRank(rank), SUIT_LETTER[suit]!, face);
}

/** The same, for a rank and suit that did not come from a dealt card. */
export function faceFor(rank: string, suit: string, face: Face): string {
  const key = `${face}:${rank}${suit}`;
  let svgText = cache.get(key);
  if (svgText === undefined) {
    svgText = face === "full" ? fullFace(rank, suit) : compactFace(rank, suit);
    cache.set(key, svgText);
  }
  return svgText;
}

/** The 52, in the order a deck is dealt from. For the contact sheet. */
export const DECK: readonly number[] = Array.from({ length: 52 }, (_, i) => i);

/** Which face a card at this size wants. The hero's two cards, and nothing else. */
export function faceForKind(kind: string): Face {
  return kind === "hero" || kind === "bj-hero" ? "full" : "compact";
}
